<script setup>
import {
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
  useTemplateRef,
  ref,
  getCurrentInstance,
} from 'vue';
import Icon from 'next/icon/Icon.vue';
import { downloadFile } from '@chatwoot/utils';
import { useEmitter } from 'dashboard/composables/emitter';
import { emitter } from 'shared/helpers/mitt';

const { attachment } = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
  showTranscribedText: {
    type: Boolean,
    default: true,
  },
});

defineOptions({
  inheritAttrs: false,
});

const loadAttempt = ref(0);
const sourceURL = computed(() => attachment.dataUrl);
const audioURL = computed(() => {
  if (!sourceURL.value) return '';
  const url = new URL(sourceURL.value);
  url.searchParams.set('t', `${Date.now()}-${loadAttempt.value}`);
  return url.toString();
});

const audioPlayer = useTemplateRef('audioPlayer');

const isPlaying = ref(false);
const isMuted = ref(false);
const currentTime = ref(0);
const duration = ref(0);
const playbackSpeed = ref(1);
const isLoading = ref(true);
const hasError = ref(false);
let retryTimer;
let retryCount = 0;
let playbackRequest = 0;

const clearRetry = () => clearTimeout(retryTimer);

const resetPlayer = () => {
  playbackRequest += 1;
  isPlaying.value = false;
  isLoading.value = true;
  hasError.value = false;
  currentTime.value = 0;
  const metadataDuration = Number(attachment.duration);
  duration.value = Number.isFinite(metadataDuration) ? metadataDuration : 0;
  audioPlayer.value.load();
  audioPlayer.value.playbackRate = playbackSpeed.value;
  audioPlayer.value.muted = isMuted.value;
};

const retryLoading = () => {
  clearRetry();
  retryCount = 0;
  loadAttempt.value += 1;
};

const onAudioError = () => {
  clearRetry();
  isPlaying.value = false;
  if (retryCount >= 4) {
    isLoading.value = false;
    hasError.value = true;
    return;
  }
  isLoading.value = true;
  retryCount += 1;
  retryTimer = setTimeout(() => {
    loadAttempt.value += 1;
  }, 1500 * retryCount);
};

const { uid } = getCurrentInstance();

const onLoadedMetadata = () => {
  const playerDuration = audioPlayer.value?.duration;
  const seconds =
    Number.isFinite(playerDuration) && playerDuration > 0
      ? playerDuration
      : Number(attachment.duration);
  if (Number.isFinite(seconds) && seconds > 0) duration.value = seconds;
};

const onCanPlay = () => {
  clearRetry();
  retryCount = 0;
  isLoading.value = false;
  hasError.value = false;
  onLoadedMetadata();
};

const playbackSpeedLabel = computed(() => {
  return `${playbackSpeed.value}x`;
});

watch(sourceURL, () => {
  clearRetry();
  retryCount = 0;
});
watch(audioURL, resetPlayer, { flush: 'post' });
watch(() => attachment.duration, onLoadedMetadata);
onMounted(resetPlayer);
onBeforeUnmount(() => {
  clearRetry();
  playbackRequest += 1;
  audioPlayer.value.pause();
});

// Listen for global audio play events and pause if it's not this audio
useEmitter('pause_playing_audio', currentPlayingId => {
  if (currentPlayingId !== uid && !audioPlayer.value?.paused) {
    playbackRequest += 1;
    try {
      audioPlayer.value.pause();
    } catch {
      /* ignore pause errors */
    }
    isPlaying.value = false;
    isLoading.value = false;
  }
});

const formatTime = time => {
  if (!Number.isFinite(time) || time <= 0) return '00:00';
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

const toggleMute = () => {
  audioPlayer.value.muted = !audioPlayer.value.muted;
  isMuted.value = audioPlayer.value.muted;
};

const onTimeUpdate = () => {
  currentTime.value = audioPlayer.value?.currentTime;
};

const seek = event => {
  const time = Number(event.target.value);
  audioPlayer.value.currentTime = time;
  currentTime.value = time;
};

const playOrPause = async () => {
  if (hasError.value) {
    retryLoading();
    return;
  }
  if (isLoading.value) return;
  if (isPlaying.value) {
    audioPlayer.value.pause();
    isPlaying.value = false;
  } else {
    // Emit event to pause all other audio
    emitter.emit('pause_playing_audio', uid);
    playbackRequest += 1;
    const request = playbackRequest;
    isLoading.value = true;
    try {
      await audioPlayer.value.play();
      if (request !== playbackRequest) return;
      isPlaying.value = true;
      isLoading.value = false;
    } catch (error) {
      if (request !== playbackRequest) return;
      isPlaying.value = false;
      if (['AbortError', 'NotAllowedError'].includes(error.name)) {
        isLoading.value = false;
      } else if (!audioPlayer.value.error) {
        onAudioError();
      }
    }
  }
};

const onEnd = () => {
  isPlaying.value = false;
  currentTime.value = 0;
  playbackSpeed.value = 1;
  audioPlayer.value.playbackRate = 1;
};

const changePlaybackSpeed = () => {
  const speeds = [1, 1.5, 2];
  const currentIndex = speeds.indexOf(playbackSpeed.value);
  const nextIndex = (currentIndex + 1) % speeds.length;
  playbackSpeed.value = speeds[nextIndex];
  audioPlayer.value.playbackRate = playbackSpeed.value;
};

const downloadAudio = async () => {
  const { fileType, dataUrl, extension } = attachment;
  downloadFile({ url: dataUrl, type: fileType, extension });
};
</script>

<template>
  <audio
    ref="audioPlayer"
    :src="audioURL"
    preload="auto"
    controls
    class="hidden"
    playsinline
    @loadedmetadata="onLoadedMetadata"
    @durationchange="onLoadedMetadata"
    @canplay="onCanPlay"
    @error="onAudioError"
    @timeupdate="onTimeUpdate"
    @ended="onEnd"
  />
  <div
    v-bind="$attrs"
    class="rounded-xl w-full gap-2 p-1.5 bg-n-alpha-white flex flex-col items-center border border-n-container shadow-[0px_2px_8px_0px_rgba(94,94,94,0.06)]"
  >
    <div class="flex gap-1 w-full flex-1 items-center justify-start">
      <button
        type="button"
        class="p-0 border-0 size-8"
        :disabled="isLoading"
        :aria-busy="isLoading"
        :aria-label="
          hasError ? $t('COMPONENTS.MEDIA.RETRY_LOADING') : undefined
        "
        @click="playOrPause"
      >
        <Icon
          v-if="isLoading"
          class="size-5 animate-spin"
          icon="i-lucide-loader-circle"
        />
        <Icon v-else-if="hasError" class="size-5" icon="refresh-outline" />
        <Icon
          v-else-if="isPlaying"
          class="size-8"
          icon="i-teenyicons-pause-small-solid"
        />
        <Icon v-else class="size-8" icon="i-teenyicons-play-small-solid" />
      </button>
      <div class="tabular-nums text-xs">
        <template v-if="isLoading && !duration">
          {{ $t('COMPONENTS.MEDIA.LOADING') }}
        </template>
        <template v-else-if="hasError">
          {{ $t('COMPONENTS.MEDIA.RETRY_LOADING') }}
        </template>
        <template v-else>
          {{ formatTime(currentTime) }} /
          {{ duration ? formatTime(duration) : '—' }}
        </template>
      </div>
      <div class="flex-1 items-center flex px-2">
        <input
          type="range"
          min="0"
          :max="duration"
          :value="currentTime"
          :disabled="isLoading || hasError || !duration"
          class="w-full h-1 bg-n-slate-12/40 rounded-lg appearance-none cursor-pointer accent-current"
          @input="seek"
        />
      </div>
      <button
        class="border-0 w-10 h-6 grid place-content-center bg-n-alpha-2 hover:bg-alpha-3 rounded-2xl"
        @click="changePlaybackSpeed"
      >
        <span class="text-xs text-n-slate-11 font-medium">
          {{ playbackSpeedLabel }}
        </span>
      </button>
      <button
        class="p-0 border-0 size-8 grid place-content-center"
        @click="toggleMute"
      >
        <Icon v-if="isMuted" class="size-4" icon="i-lucide-volume-off" />
        <Icon v-else class="size-4" icon="i-lucide-volume-2" />
      </button>
      <button
        class="p-0 border-0 size-8 grid place-content-center"
        @click="downloadAudio"
      >
        <Icon class="size-4" icon="i-lucide-download" />
      </button>
    </div>

    <div
      v-if="attachment.transcribedText && showTranscribedText"
      class="text-n-slate-12 p-3 text-sm bg-n-alpha-1 rounded-lg w-full break-words"
    >
      {{ attachment.transcribedText }}
    </div>
  </div>
</template>
